import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../services/auth.service';
import { MaintenanceStatusService } from '../../services/maintenance-status.service';

@Component({
  selector: 'app-maintenance-banner',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (status() && status()!.active) {
      <div class="maintenance-banner maintenance-banner--active">
        <span class="me-2">🛠</span>
        @if (status()!.source === 'system') {
          <strong>System-Wartungsmodus aktiv:</strong>
        } @else {
          <strong>Wartungsmodus für deinen Mandant aktiv:</strong>
        }
        <span class="ms-2">{{ status()!.message ?? 'Schreib-Operationen sind blockiert.' }}</span>
      </div>
    } @else if (status() && status()!.scheduled) {
      <div class="maintenance-banner maintenance-banner--scheduled">
        <span class="me-2">⏱</span>
        <strong>Geplante Wartung in {{ countdown() }}:</strong>
        <span class="ms-2">{{ status()!.message ?? 'Bitte rechtzeitig speichern.' }}</span>
      </div>
    }
  `,
  styles: [
    `
    .maintenance-banner {
      position: sticky; top: 0; z-index: 1100;
      width: 100%; padding: 0.5rem 1rem;
      text-align: center; font-size: 0.95rem;
    }
    .maintenance-banner--active   { background: #FFD580; color: #5C3A00; }
    .maintenance-banner--scheduled{ background: #BEE3F8; color: #2C5282; }
  `,
  ],
})
export class MaintenanceBannerComponent implements OnInit {
  private readonly svc = inject(MaintenanceStatusService);
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  readonly status = this.svc.status;
  private readonly tick = signal(0);

  readonly countdown = computed(() => {
    this.tick(); // re-evaluate when tick changes
    const s = this.status();
    if (!s?.scheduled || s.secondsUntilStart === null) return '';
    // Deduct elapsed seconds from server snapshot
    const elapsed = Math.floor(this.tick() / 1000);
    const remaining = Math.max(0, s.secondsUntilStart - elapsed);
    return this.formatDuration(remaining);
  });

  ngOnInit(): void {
    // Schutz: Banner-Polling nur fuer eingeloggte User. /maintenance/status
    // ist IS_AUTHENTICATED_FULLY-geschuetzt; ein 401-Loop wuerde hier sonst
    // den Login-Flow stoeren (Banner ist im default-layout gemountet, das
    // nach Login geladen wird — defensive Guard fuer Edge-Cases).
    if (this.auth.isAuthenticated()) {
      this.svc.refresh().subscribe({
        error: () => {
          /* ignore */
        },
      });
    }
    const interval = setInterval(() => this.tick.update((v) => v + 1000), 1000);
    this.destroyRef.onDestroy(() => clearInterval(interval));
  }

  private formatDuration(seconds: number): string {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return h > 0 ? `${h}h ${m}min` : m > 0 ? `${m}min ${s}s` : `${s}s`;
  }
}
